import { Box, Button, Icon, Modal, TextInput } from '@palmetto/palmetto-components';
import { useMemo, useState } from 'react';

import { QueueDocument, QueueType, QueueTypeLabel } from 'types';

interface ReleaseHoldMotionModalProps {
  isOpen: boolean;
  handleClose: () => void;
  handleReleaseHoldMotion: () => void;
  isLoading: boolean;
  projectsToReleaseTotal: number;
  selectedQueues?: QueueDocument[];
  isReleaseModeAll?: boolean;
  includesActivationPackage?: boolean;
}

const PLURALITY_THRESHOLD = 1;
export const ReleaseHoldMotionModal = ({
  isOpen,
  handleClose,
  handleReleaseHoldMotion,
  isLoading,
  projectsToReleaseTotal,
  selectedQueues,
  isReleaseModeAll,
  includesActivationPackage,
}: ReleaseHoldMotionModalProps) => {
  const [releaseTextConfirmation, setReleaseTextConfirmation] = useState('');

  const showActivationReleasesPtoWarning = isReleaseModeAll
    ? Boolean(includesActivationPackage)
    : Boolean(selectedQueues?.some((queue) => queue?.type === QueueType.activationPackage));

  const allQueueMilestoneTypesAndAffectedOrganizations =
    selectedQueues?.flatMap((queue) => ({
      queueTypes: queue?.type,
      affectedOrganizations: queue?.account?.organizationName,
    })) || [];
  const releaseDescription = isReleaseModeAll
    ? 'All held packages will receive approval notifications immediately.'
    : 'The selected packages will receive approval notifications immediately.';

  const organizationsAffected = useMemo(() => {
    const organizationNames = allQueueMilestoneTypesAndAffectedOrganizations
      .map((item) => item.affectedOrganizations)
      .filter((organizationName): organizationName is string => Boolean(organizationName));
    const orgCount: Record<string, number> = {};
    organizationNames.forEach((name) => {
      orgCount[name] = (orgCount[name] || 0) + 1;
    });
    return Object.entries(orgCount)
      .map(([name, count]) => `${name} (${count})`)
      .join(', ');
  }, [allQueueMilestoneTypesAndAffectedOrganizations, selectedQueues]);

  const milestonesAffected = useMemo(() => {
    const milestoneCounts = allQueueMilestoneTypesAndAffectedOrganizations.reduce<Record<string, number>>(
      (acc, item) => {
        const label = QueueTypeLabel[item.queueTypes];
        if (label) {
          acc[label] = (acc[label] ?? 0) + 1;
        }
        return acc;
      },
      {},
    );

    const milestonesAffected = Object.entries(milestoneCounts)
      .map(([label, count]) => `${label} (${count})`)
      .join(', ');

    return milestonesAffected;
  }, [allQueueMilestoneTypesAndAffectedOrganizations]);
  const projectsText = projectsToReleaseTotal === PLURALITY_THRESHOLD ? 'Project' : 'Projects';
  const confirmationText = `Release ${projectsText}`;

  const handleReleaseHoldMotionClick = () => {
    setReleaseTextConfirmation('');
    handleReleaseHoldMotion();
  };

  const handleCloseClick = () => {
    setReleaseTextConfirmation('');
    handleClose();
  };

  return (
    <Modal isOpen={isOpen} onDismiss={() => {}} maxWidth="4xl" ariaLabelledBy="releaseHoldMotionModal">
      <Box direction="column" gap="md" data-testid="release-hold-motion-modal-header" margin="lg">
        <Box
          width="50px"
          height="50px"
          radius="circle"
          background="success-100"
          borderWidth="2px"
          borderColor="grey-600"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Box width="27px" height="27px" alignItems="center" justifyContent="center">
            <Icon name="check" size="xl" color="primary" />
          </Box>
        </Box>
        <Box fontWeight="bold" fontSize="xl">
          {confirmationText}?
        </Box>
        <Box display="block">{releaseDescription} Approvals will enter the payout batching pipeline.</Box>
        {showActivationReleasesPtoWarning && (
          <Box
            display="block"
            radius="md"
            borderWidth="xs"
            borderColor="danger-400"
            color="danger-600"
            padding="md"
            background="danger-100"
          >
            <strong>Warning:</strong> Releasing the Activation Package will also release any held PTO Package +
            payments on this project.
          </Box>
        )}
        <Box
          direction="row"
          justifyContent="space-between"
          background="grey-50"
          radius="md"
          padding="md"
          color="grey-600"
        >
          <Box>{projectsText} to release</Box>
          <Box fontSize="lg" fontWeight="bold">
            {projectsToReleaseTotal}
          </Box>
        </Box>
        <Box gap="md">
          <Box display="block">
            Write the following text in the box below to release {projectsText.toLowerCase()}:{' '}
            <strong>{confirmationText}</strong>
          </Box>
          <TextInput
            id="confirmReleaseHoldMotion"
            name="confirmReleaseHoldMotion"
            label=""
            placeholder={`Type '${confirmationText}' to confirm`}
            value={releaseTextConfirmation}
            onChange={(event) => setReleaseTextConfirmation(event.target.value)}
            isRequired
          />
        </Box>
        {!isReleaseModeAll && (
          <Box direction="column" gap="xs">
            <Box direction="row" gap="sm">
              <Box fontWeight="bold">Licensed Orgs affected:</Box>
              <Box as="span">{organizationsAffected}</Box>
            </Box>
            <Box direction="row" gap="sm">
              <Box as="span" fontWeight="bold">
                Milestones:
              </Box>
              <Box as="span">{milestonesAffected}</Box>
            </Box>
          </Box>
        )}
        <Box direction="row" gap="md" justifyContent="flex-end">
          <Button variant="secondary" tone="neutral" type="button" onClick={handleCloseClick} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button
            tone={'primary'}
            type="button"
            onClick={handleReleaseHoldMotionClick}
            isDisabled={
              isLoading ||
              projectsToReleaseTotal < 1 ||
              releaseTextConfirmation.toLowerCase() !== confirmationText.toLowerCase()
            }
          >
            {confirmationText}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
